import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "../../../../components/header/Header";

const PartnershipPage = () => {
  return (
    <>
      <Helmet>
        <title> Terms and Conditions || Octy AI</title>
      </Helmet>
      {/* End Page SEO Content */}
      <div>
        <h2 id="octy-ai-terms-of-service">Octy.ai Terms of Service</h2>
        <p>
          <strong>Last Updated: May 10, 2025</strong>
        </p>
        <h3 id="1-introduction">1. Introduction</h3>
        <p>
          Welcome to Octy.ai. These Terms of Service (&quot;Terms&quot;) govern
          your access to and use of the Octy.ai application, website, and
          services (collectively, the &quot;Service&quot;). By accessing or
          using the Service, you agree to be bound by these Terms. If you
          disagree with any part of the Terms, you may not access the Service.
        </p>
        <p>
          Octy.ai (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) provides
          an AI-powered customer retention and analytics platform that
          integrates with Shopify stores.
        </p>
        <h3 id="2-definitions">2. Definitions</h3>
        <ul>
          <li>
            <strong>Service</strong>: The Octy.ai application, APIs, website,
            and related services.
          </li>
          <li>
            <strong>Merchant</strong>: Any Shopify store owner or authorized
            representative who installs the Octy.ai app.
          </li>
          <li>
            <strong>End User</strong>: Any individual who visits or interacts
            with a Merchant&#39;s Shopify store where Octy.ai is installed.
          </li>
          <li>
            <strong>Data</strong>: Any information collected, processed, or
            generated through the use of the Service.
          </li>
          <li>
            <strong>Personal Information</strong>: Information that identifies
            or can be used to identify an individual End User.
          </li>
        </ul>
        <h3 id="3-account-terms">3. Account Terms</h3>
        <h3 id="3-1-registration-requirements">
          3.1 Registration Requirements
        </h3>
        <p>To use Octy.ai, you must:</p>
        <ul>
          <li>Be at least 18 years of age</li>
          <li>Complete the registration process</li>
          <li>Provide accurate and complete information</li>
          <li>Accept these Terms and our Privacy Policy</li>
        </ul>
        <h3 id="3-2-account-responsibilities">3.2 Account Responsibilities</h3>
        <p>You are responsible for:</p>
        <ul>
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activities that occur under your account</li>
          <li>Notifying us immediately of any unauthorized access</li>
        </ul>
        <h3 id="3-3-account-types">3.3 Account Types</h3>
        <p>
          Your account and billing relationship will be with Octy.ai. Your use
          of the Shopify platform is governed by your agreement with Shopify.
        </p>
        <h3 id="4-service-terms">4. Service Terms</h3>
        <h3 id="4-1-service-license">4.1 Service License</h3>
        <p>
          Subject to your compliance with these Terms, we grant you a limited,
          non-exclusive, non-transferable, revocable license to access and use
          the Service.
        </p>
        <h3 id="4-2-technical-requirements">4.2 Technical Requirements</h3>
        <p>
          The Service requires integration with Shopify and may have specific
          technical requirements that we will communicate during installation or
          as updates occur.
        </p>
        <h3 id="4-3-service-availability">4.3 Service Availability</h3>
        <p>
          We strive to ensure the Service is available 24/7, but do not
          guarantee uninterrupted access. Scheduled maintenance will be
          communicated in advance whenever possible.
        </p>
        <h3 id="4-4-support">4.4 Support</h3>
        <p>
          Support is provided according to your subscription plan. All users
          receive access to documentation and email support.
        </p>
        <h3 id="5-merchant-obligations">5. Merchant Obligations</h3>
        <h3 id="5-1-acceptable-use">5.1 Acceptable Use</h3>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any illegal purpose</li>
          <li>Attempt to gain unauthorized access to the Service</li>
          <li>Interfere with or disrupt the integrity of the Service</li>
          <li>
            Reproduce, duplicate, copy, sell, or resell any portion of the
            Service
          </li>
          <li>
            Use the Service in a manner that exceeds reasonable request volume
            limits
          </li>
        </ul>
        <h3 id="5-2-end-user-consent">5.2 End User Consent</h3>
        <p>You are responsible for:</p>
        <ul>
          <li>
            Providing appropriate notice to your End Users about data collection
          </li>
          <li>Obtaining necessary consents from End Users</li>
          <li>Maintaining a clear and accessible privacy policy</li>
          <li>
            Ensuring your use of our Service complies with applicable laws and
            regulations
          </li>
        </ul>
        <h3 id="5-3-data-usage-rights">5.3 Data Usage Rights</h3>
        <p>
          By using our Service, you grant us the right to collect, process, and
          analyze data from your Shopify store to provide and improve our
          Service.
        </p>
        <h3 id="6-data-and-privacy">6. Data and Privacy</h3>
        <h3 id="6-1-data-collection">6.1 Data Collection</h3>
        <p>
          We collect data as described in our Privacy Policy. This includes:
        </p>
        <ul>
          <li>Store data provided through Shopify&#39;s API</li>
          <li>End User behavioral data</li>
          <li>Analytics and performance data</li>
        </ul>
        <h3 id="6-2-data-ownership">6.2 Data Ownership</h3>
        <ul>
          <li>You own all rights to your store data</li>
          <li>
            We own all rights to the algorithms, models, and analytical tools we
            develop
          </li>
          <li>
            We own aggregate, anonymized data derived from cross-merchant
            analysis
          </li>
        </ul>
        <h3 id="6-3-data-security">6.3 Data Security</h3>
        <p>
          We implement and maintain reasonable security measures to protect your
          data.
        </p>
        <h3 id="6-4-data-deletion">6.4 Data Deletion</h3>
        <p>Upon termination of your account, we will:</p>
        <ul>
          <li>Delete or anonymize your store&#39;s data within 90 days</li>
          <li>Retain anonymized data for analytical purposes</li>
        </ul>
        <h3 id="7-billing-and-payment">7. Billing and Payment</h3>
        <h3 id="7-1-subscription-plans">7.1 Subscription Plans</h3>
        <ul>
          <li>
            Pricing is based on subscription plans as published on our website
          </li>
          <li>Charges are billed in advance on a monthly or annual basis</li>
          <li>No refunds will be issued for partial months of service</li>
        </ul>
        <h3 id="7-2-payment-processing">7.2 Payment Processing</h3>
        <ul>
          <li>Payments are processed through Shopify&#39;s billing system</li>
          <li>
            You authorize us to charge your payment method for all applicable
            fees
          </li>
        </ul>
        <h3 id="7-3-plan-changes">7.3 Plan Changes</h3>
        <ul>
          <li>You may upgrade your plan at any time</li>
          <li>
            Downgrades will take effect at the end of the current billing cycle
          </li>
        </ul>
        <h3 id="7-4-fee-changes">7.4 Fee Changes</h3>
        <p>We reserve the right to modify our fees upon 30 days&#39; notice.</p>
        <h3 id="8-term-and-termination">8. Term and Termination</h3>
        <h3 id="8-1-term">8.1 Term</h3>
        <p>
          These Terms will remain in effect until terminated by either party.
        </p>
        <h3 id="8-2-termination-by-merchant">8.2 Termination by Merchant</h3>
        <p>You may terminate these Terms at any time by:</p>
        <ul>
          <li>Uninstalling the Octy.ai app from your Shopify store</li>
          <li>Cancelling your subscription through your account settings</li>
        </ul>
        <h3 id="8-3-termination-by-octy-ai">8.3 Termination by Octy.ai</h3>
        <p>
          We may terminate or suspend your access immediately, without prior
          notice, if:
        </p>
        <ul>
          <li>You breach these Terms</li>
          <li>You fail to pay subscription fees</li>
          <li>We are required to do so by law</li>
          <li>We decide to discontinue the Service</li>
        </ul>
        <h3 id="8-4-effects-of-termination">8.4 Effects of Termination</h3>
        <p>Upon termination:</p>
        <ul>
          <li>All rights and licenses granted will terminate</li>
          <li>
            You will lose access to all data not otherwise available in your
            Shopify store
          </li>
          <li>
            Sections that by their nature should survive will remain in effect
          </li>
        </ul>
        <h3 id="9-intellectual-property">9. Intellectual Property</h3>
        <h3 id="9-1-our-intellectual-property">
          9.1 Our Intellectual Property
        </h3>
        <p>
          Octy.ai and its original content, features, and functionality are
          owned by us and are protected by international copyright, trademark,
          and other intellectual property laws.
        </p>
        <h3 id="9-2-feedback">9.2 Feedback</h3>
        <p>
          If you provide feedback on the Service, you grant us an unlimited,
          perpetual right to use that feedback without compensation.
        </p>
        <h3 id="10-disclaimers-and-limitations">
          10. Disclaimers and Limitations
        </h3>
        <h3 id="10-1-service-warranty-disclaimer">
          10.1 Service Warranty Disclaimer
        </h3>
        <p>
          THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;
          WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
        </p>
        <h3 id="10-2-limitation-of-liability">10.2 Limitation of Liability</h3>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL OCTY.AI BE
          LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
          PUNITIVE DAMAGES.
        </p>
        <h3 id="10-3-aggregate-liability-cap">10.3 Aggregate Liability Cap</h3>
        <p>
          OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS SHALL NOT
          EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICE DURING THE TWELVE (12)
          MONTHS PRECEDING THE CLAIM.
        </p>
        <h3 id="11-indemnification">11. Indemnification</h3>
        <p>
          You agree to indemnify and hold harmless Octy.ai, its affiliates,
          officers, directors, employees, and agents from any claims,
          liabilities, damages, losses, and expenses arising from your use of
          the Service.
        </p>
        <h3 id="12-general-provisions">12. General Provisions</h3>
        <h3 id="12-1-governing-law">12.1 Governing Law</h3>
        <p>
          These Terms shall be governed by the laws of [Your Jurisdiction],
          without regard to its conflict of law provisions.
        </p>
        <h3 id="12-2-dispute-resolution">12.2 Dispute Resolution</h3>
        <p>
          Any dispute arising from these Terms shall be resolved through binding
          arbitration in Toronto, Ontario, Canada.
        </p>
        <h3 id="12-3-severability">12.3 Severability</h3>
        <p>
          If any provision of these Terms is found to be unenforceable, the
          remaining provisions will remain in effect.
        </p>
        <h3 id="12-4-changes-to-terms">12.4 Changes to Terms</h3>
        <p>
          We reserve the right to modify these Terms at any time. Changes will
          be effective upon posting to our website with reasonable notice.
        </p>
        <h3 id="12-5-assignment">12.5 Assignment</h3>
        <p>
          You may not assign these Terms without our prior written consent. We
          may assign these Terms without restriction.
        </p>
        <h3 id="12-6-entire-agreement">12.6 Entire Agreement</h3>
        <p>
          These Terms constitute the entire agreement between you and Octy.ai
          regarding the Service.
        </p>
      </div>
    </>
  );
};

export default PartnershipPage;
